/**
 * The form on /contact. It is checked with the same rules the API uses
 * (submission.ts), and a problem is shown under the field it is about.
 */

import { newSubmissionId, submit, SubmitError } from './feedback-client.js';
import { HONEYPOT_FIELD, parseSubmission, type SubmissionField } from './submission.js';

const FIELDS = ['name', 'email', 'subject', 'message'] as const;

function fieldValue(form: HTMLFormElement, name: string): string {
  const value = new FormData(form).get(name);
  return typeof value === 'string' ? value : '';
}

function showFieldError(form: HTMLFormElement, field: SubmissionField | undefined, message: string): boolean {
  const input = form.querySelector<HTMLElement>(`#contact-${field}`);
  const error = form.querySelector(`#contact-${field}-error`);
  if (!input || !error) return false;
  error.textContent = message;
  input.setAttribute('aria-invalid', 'true');
  input.setAttribute('aria-describedby', error.id);
  input.focus();
  return true;
}

function clearErrors(form: HTMLFormElement, status: Element): void {
  status.textContent = '';
  for (const field of FIELDS) {
    const input = form.querySelector(`#contact-${field}`);
    input?.removeAttribute('aria-invalid');
    input?.removeAttribute('aria-describedby');
    const error = form.querySelector(`#contact-${field}-error`);
    if (error) error.textContent = '';
  }
}

function init(): void {
  const form = document.querySelector<HTMLFormElement>('.contact-form');
  const sent = document.querySelector<HTMLElement>('.contact-sent');
  const status = form?.querySelector('.fb-status');
  const sendButton = form?.querySelector<HTMLButtonElement>('.fb-submit');
  if (!form || !sent || !status || !sendButton) return;

  // Kept across failed tries, so a retry can never send the message twice.
  const id = newSubmissionId();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    clearErrors(form, status);
    const parsed = parseSubmission({
      kind: 'contact',
      id,
      name: fieldValue(form, 'name'),
      email: fieldValue(form, 'email'),
      subject: fieldValue(form, 'subject'),
      message: fieldValue(form, 'message'),
    });
    if (!parsed.ok) {
      showFieldError(form, parsed.field, parsed.error);
      return;
    }
    sendButton.disabled = true;
    sendButton.textContent = 'Sending…';
    submit(parsed.submission, fieldValue(form, HONEYPOT_FIELD))
      .then(() => {
        form.hidden = true;
        sent.hidden = false;
        sent.focus();
      })
      .catch((error: unknown) => {
        const message = error instanceof SubmitError ? error.message : 'Could not send. Try again.';
        const field = error instanceof SubmitError ? error.field : undefined;
        if (!showFieldError(form, field, message)) status.textContent = message;
      })
      .finally(() => {
        sendButton.disabled = false;
        sendButton.textContent = 'Send message';
      });
  });
}

init();
